import { useState } from 'react';
import { fetchAllCalls, useCallFunnelQuery, useCallTimeseriesQuery, useCallsQuery } from '../api/queries';
import { toCsv, downloadCsv } from '../lib/csv';
import type { Call } from '../types';
import CallDetailDrawer from './calls/CallDetailDrawer';
import CallFilters from './calls/CallFilters';
import CallFunnelChart from './calls/CallFunnelChart';
import CallVolumeChart from './calls/CallVolumeChart';
import CallsStats from './calls/CallsStats';
import CallsTable from './calls/CallsTable';
import type { CallFilters as Filters, CallSortField } from '../types';
import { callSourceLabel } from '../lib/callSource';

type Props = { af: string | null };

const PER_PAGE = 25;

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function jan1Iso(): string {
  return `${new Date().getFullYear()}-01-01`;
}

function csvDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function callStatus(c: Call): string {
  if (c.voicemail) return 'Voicemail';
  if (c.ivrDetected) return 'IVR Only';
  if (c.answered) return 'Answered';
  return 'Missed';
}

function exportCallsCsv(af: string, calls: Call[]): void {
  const headers = [
    'Date', 'Name', 'Phone', 'City', 'State', 'Status', 'Duration (sec)',
    'Source', 'Tracking Number', 'Sentiment', 'Lead Status', 'Tags',
  ];
  const rows = calls.map((c) => [
    csvDate(c.startTime),
    c.customerName ?? '',
    c.customerPhone ?? '',
    c.customerCity ?? '',
    c.customerState ?? '',
    callStatus(c),
    c.duration ?? 0,
    callSourceLabel(c),
    c.trackingPhoneNumber ?? '',
    c.callrailSentiment ?? '',
    c.callrailLeadStatus ?? '',
    (c.callrailTags ?? []).join('; '),
  ]);
  const stamp = new Date().toISOString().slice(0, 10);
  downloadCsv(`call-leads_${af}_${stamp}.csv`, toCsv(headers, rows));
}

export default function CallTrackingTab({ af }: Props) {
  const [filters, setFilters] = useState<Filters>({
    search: '',
    status: '',
    sentiment: '',
    dateFrom: jan1Iso(),
    dateTo: todayIso(),
  });
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<CallSortField>('start_time');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedCallId, setSelectedCallId] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);

  const funnelQ = useCallFunnelQuery(af, filters.dateFrom, filters.dateTo);
  const timeseriesQ = useCallTimeseriesQuery(af, filters.dateFrom, filters.dateTo, 'week');
  const callsQ = useCallsQuery({
    af, page, perPage: PER_PAGE, sortBy, sortOrder, filters,
  });

  if (!af) {
    return (
      <div className="tab-placeholder">
        <h3>Call Tracking</h3>
        <p>Select a franchisee to view their call data</p>
      </div>
    );
  }

  const isLoading = funnelQ.isLoading || timeseriesQ.isLoading || callsQ.isLoading;
  const error = funnelQ.error || timeseriesQ.error || callsQ.error;

  if (isLoading && !funnelQ.data && !callsQ.data) {
    return (
      <div className="listings-loading">
        <div className="spinner" />
        Loading call data...
      </div>
    );
  }
  if (error && !funnelQ.data) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: '#c0392b' }}>
        Error loading call data: {(error as Error).message}
      </div>
    );
  }

  const handleFilterChange = (next: Filters) => {
    setFilters(next);
    setPage(1);
  };
  const handleExport = async () => {
    if (!af || exporting) return;
    setExporting(true);
    try {
      const calls = await fetchAllCalls({ af, sortBy, sortOrder, filters });
      exportCallsCsv(af, calls);
    } catch (e) {
      console.error('Call CSV export failed', e);
      alert('Sorry — the CSV export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleSort = (field: CallSortField) => {
    if (field === sortBy) {
      setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const calls = callsQ.data?.data ?? [];
  const total = callsQ.data?.total ?? 0;
  const totalPages = callsQ.data?.totalPages ?? 1;
  const stage = funnelQ.data?.current ?? {};

  return (
    <>
      {funnelQ.data && <CallsStats funnel={funnelQ.data} />}

      <div className="charts-row">
        <div className="chart-panel">
          <h3>Call Volume</h3>
          <div className="chart-container">
            {timeseriesQ.data && <CallVolumeChart data={timeseriesQ.data} />}
          </div>
        </div>
        <div className="chart-panel">
          <h3>Conversion Funnel</h3>
          <div className="chart-container">
            <CallFunnelChart stage={stage} />
          </div>
        </div>
      </div>

      <CallFilters filters={filters} onChange={handleFilterChange} />

      <div className="calls-toolbar">
        <button
          type="button"
          className="export-csv-btn"
          onClick={handleExport}
          disabled={exporting || total === 0}
          title={total === 0 ? 'No calls to export' : 'Download all calls matching the current filters'}
        >
          {exporting ? 'Preparing CSV…' : '⬇ Download CSV'}
        </button>
      </div>

      <CallsTable
        af={af}
        calls={calls}
        total={total}
        page={page}
        perPage={PER_PAGE}
        totalPages={totalPages}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        onPage={setPage}
        onSelect={setSelectedCallId}
      />

      <CallDetailDrawer callId={selectedCallId} onClose={() => setSelectedCallId(null)} />
    </>
  );
}
